import React, {FC, useEffect, useMemo, useRef, useState} from 'react'
import {toast, ToastContainer} from 'react-toastify'
import {KTSVG} from '../../../../_metronic/helpers'
import {useAuth} from '../../auth'
import {getCompanyAdminList, getCompanyList} from '../../Management/core/_requests'
import type {Vessel} from '../../Management/core/_models'
import {fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels} from '../../../shared/access/accessibleVessels'
import type {QHSEWorkPermitDto, WorkPermitRecord} from '../core/_models'
import {
  searchWorkPermits,
  createWorkPermit,
  updateWorkPermit,
  submitWorkPermit,
  approveWorkPermit,
  closeWorkPermit,
  uploadWorkPermitAttachments,
  listWorkPermitAttachments,
  workPermitAttachmentViewUrl,
  workPermitAttachmentDownloadUrl,
  type WorkPermitAttachmentType,
} from '../core/_requests'
import {WorkPermitFormModal, WorkPermitFormState, WorkPermitUploadFiles} from './WorkPermitFormModal'
import WorkPermitAttachmentsModal from './WorkPermitAttachmentsModal'
import {ViewWorkPermitModal} from './ViewWorkPermitModal'
import {FileViewerModal} from './FileViewerModal'
import {QHSE_PERMS} from '../core/permissions'
import {
  filterVesselsForManagementList,
  getUserCompanyGroupAdminId,
  isBroadFleetPickerUser,
  isLikelyVesselAssignedUser,
} from '../core/qhseUserContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import { TableSkeleton } from '../../../shared/components/LoadingSkeletons'

type CompanyGroup = {id: number; name: string}
type Subcompany = {id: number; name: string; companyId: number}

type AttachmentModalState = {
  visible: boolean
  loading: boolean
  title: string
  subtitle?: string | null
  items: {
    id: string
    fileName: string
    viewUrl: string
    downloadUrl: string
    type: WorkPermitAttachmentType
    typeLabel: string
    sizeBytes?: number
    mimeType?: string | null
  }[]
}

const tableOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  background: 'rgba(255,255,255,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 5,
}

const spinner = (
  <div className='spinner-border' role='status' aria-label='Loading'>
    <span className='visually-hidden'>Loading...</span>
  </div>
)

const statusBadgeClass = (status?: string | null) => {
  if (!status) return 'badge-light'
  switch (status) {
    case 'SAVE':
      return 'badge-light-secondary'
    case 'SUBMIT':
      return 'badge-light-warning'
    case 'CLOSE':
      return 'badge-light-success'
    default:
      return 'badge-light'
  }
}

const WorkPermitsAndChecklistsPage: FC = () => {
  const {currentUser, can, permissionsLoaded} = useAuth()

  const shipboardUser = isLikelyVesselAssignedUser(currentUser)
  const broadFleet = isBroadFleetPickerUser(currentUser)
  const myCgaId = getUserCompanyGroupAdminId(currentUser) ?? null
  const isTopLevel = broadFleet
  const effectiveCompanyId = isTopLevel ? undefined : (myCgaId ?? undefined)

  const canReadPermit = permissionsLoaded && can(QHSE_PERMS.WORK_PERMIT_READ)
  const canCreatePermit = permissionsLoaded && can(QHSE_PERMS.WORK_PERMIT_CREATE)
  const canUpdatePermit = permissionsLoaded && can(QHSE_PERMS.WORK_PERMIT_UPDATE)
  const canSubmitPermit = permissionsLoaded && can(QHSE_PERMS.WORK_PERMIT_SUBMIT)
  const canApprovePermit = permissionsLoaded && can(QHSE_PERMS.WORK_PERMIT_APPROVE)
  const canClosePermit = permissionsLoaded && can(QHSE_PERMS.WORK_PERMIT_CLOSE)
  const canOpenPermitFiles =
    permissionsLoaded &&
    (can(QHSE_PERMS.WORK_PERMIT_ATTACH_VIEW) || can(QHSE_PERMS.WORK_PERMIT_ATTACH_DOWNLOAD))

  const [filters, setFilters] = useState<{
    companyId: string
    subcompanyId: string
    vesselId: string
    status: 'All' | 'SAVE' | 'SUBMIT' | 'CLOSE'
    fromDate: string
    toDate: string
  }>({
    companyId: '',
    subcompanyId: '',
    vesselId: '',
    status: 'All',
    fromDate: '',
    toDate: '',
  })

  const [companies, setCompanies] = useState<CompanyGroup[]>([])
  const [subcompanies, setSubcompanies] = useState<Subcompany[]>([])
  const [vesselsScoped, setVesselsScoped] = useState<Vessel[]>([])
  const [records, setRecords] = useState<WorkPermitRecord[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(false)
  const [lookupsReady, setLookupsReady] = useState(false)
  const isDataLoading = loading || !lookupsReady
  const [didAutoSelectVessel, setDidAutoSelectVessel] = useState(false)

  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const [sortColumn, setSortColumn] = useState<
    keyof WorkPermitRecord | 'attachmentsCount' | 'officeStatus' | ''
  >('startDate')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const [addOpen, setAddOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [viewOpen, setViewOpen] = useState(false)
  const [editing, setEditing] = useState<WorkPermitRecord | null>(null)
  const [viewing, setViewing] = useState<QHSEWorkPermitDto | null>(null)

  const [attachmentsModal, setAttachmentsModal] = useState<AttachmentModalState>({
    visible: false,
    loading: false,
    title: 'Attachments',
    subtitle: null,
    items: [],
  })

  const [fileModal, setFileModal] = useState<{
    visible: boolean
    title: string
    fileName?: string
    viewUrl?: string
    downloadUrl?: string
  }>({
    visible: false,
    title: '',
  })

  const isMountedRef = useRef(true)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const loadLookups = async () => {
    try {
      const [groupsRaw, adminsRaw, vesselList] = await Promise.all([
        getCompanyAdminList?.().catch(() => []),
        getCompanyList?.().catch(() => []),
        fetchAccessibleVesselsForCurrentUser().then(normalizeAccessibleVessels).catch(() => [] as Vessel[]),
      ])

      const groups: CompanyGroup[] = Array.isArray(groupsRaw)
        ? groupsRaw.map((g: any) => ({
            id: Number(g.id),
            name: g.name ?? g.companyGroupAdminName ?? `Group #${g.id}`,
          }))
        : []

      const admins: Subcompany[] = Array.isArray(adminsRaw)
        ? adminsRaw.map((a: any) => ({
            id: Number(a.id),
            name: a.name ?? a.companyAdminName ?? `Company #${a.id}`,
            companyId: Number(a.cgaid?.id ?? a.cga?.id ?? a.companyGroupAdminId ?? a.companyGroupId),
          }))
        : []

      if (!isMountedRef.current) return
      setCompanies(groups.filter((g) => Number.isFinite(g.id)))
      setSubcompanies(admins.filter((a) => Number.isFinite(a.id) && Number.isFinite(a.companyId)))

      const vesselsForUser = filterVesselsForManagementList(vesselList || [], currentUser)

      if (!isMountedRef.current) return
      setVesselsScoped(vesselsForUser)
      setLookupsReady(true)
    } catch (e) {
      console.error('Failed to load lookups', e)
      if (!isMountedRef.current) return
      setLookupsReady(true)
    }
  }

  useEffect(() => {
    loadLookups()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser])

  const { state: globalState } = useGlobalFilter()

  useEffect(() => {
    if (globalState.searchQuery !== undefined) {
      setSearchTerm(globalState.searchQuery)
    }
  }, [globalState.searchQuery])

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      vesselId: globalState.vessel?.id ? String(globalState.vessel.id) : '',
      companyId: globalState.company ? String(globalState.company.id) : '',
    }))
  }, [globalState.vessel, globalState.company])

  useEffect(() => {
    if (!lookupsReady) return
    if (shipboardUser && !didAutoSelectVessel) {
      const vesselId = currentUser?.vessel?.id
      if (vesselId) {
        setFilters((prev) => ({...prev, vesselId: String(vesselId)}))
        setDidAutoSelectVessel(true)
      }
    }
  }, [lookupsReady, shipboardUser, didAutoSelectVessel, currentUser])

  const permitNameOptions = useMemo(() => {
    const names = new Set<string>()
    records.forEach((r) => {
      if (r.permitName) names.add(r.permitName)
    })
    return Array.from(names).sort()
  }, [records])

  const vesselsForFilters = useMemo(() => {
    let list = vesselsScoped
    if (filters.companyId) {
      const cid = Number(filters.companyId)
      list = list.filter((v: any) => Number(v.companyGroupAdmin?.id ?? v.companyGroupId) === cid)
    }
    if (filters.subcompanyId) {
      const scid = Number(filters.subcompanyId)
      list = list.filter((v: any) => Number(v.companyAdmin?.id ?? v.companyId) === scid)
    }
    return list
  }, [vesselsScoped, filters.companyId, filters.subcompanyId])

  const companyIdForSubcompanyLookup = useMemo(() => {
    if (filters.companyId) return Number(filters.companyId)
    if (!isTopLevel && effectiveCompanyId) return Number(effectiveCompanyId)
    return null
  }, [filters.companyId, isTopLevel, effectiveCompanyId])

  const subcompaniesForChosenCompany = useMemo(() => {
    if (!companyIdForSubcompanyLookup) return []
    return subcompanies.filter((sc) => sc.companyId === companyIdForSubcompanyLookup)
  }, [companyIdForSubcompanyLookup, subcompanies])

  const hasSubcompaniesForChosenCompany = subcompaniesForChosenCompany.length > 0

  const loadWorkPermits = async () => {
    if (!lookupsReady) return
    setLoading(true)
    try {
      if (shipboardUser && !currentUser?.vessel?.id) {
        setRecords([])
        return
      }

      const params = {
        companyGroupId: shipboardUser
          ? undefined
          : (filters.companyId ? Number(filters.companyId) : effectiveCompanyId),
        companyId: shipboardUser ? undefined : (filters.subcompanyId ? Number(filters.subcompanyId) : undefined),
        vesselId: shipboardUser
          ? currentUser?.vessel?.id
          : (filters.vesselId ? Number(filters.vesselId) : undefined),
        status: filters.status === 'All' ? undefined : filters.status,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
      }

      const list = await searchWorkPermits(params)
      if (!isMountedRef.current) return
      setRecords(list.map(mapDtoToRecord))
    } catch (e) {
      console.error('Failed to load work permits', e)
      toast.error('Failed to load work permits', {position: 'top-center'})
      setRecords([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadWorkPermits()
  }, [lookupsReady, filters])

  const mapRecordToFormState = (r: WorkPermitRecord): WorkPermitFormState => ({
    vesselId: r.vesselId,
    permitName: r.permitName || '',
    formNumber: r.formNumber || '',
    startDate: r.startDate || '',
    startTime: r.startTime || '',
    expectedEndDate: r.expectedEndDate || '',
    expectedEndTime: r.expectedEndTime || '',
    endDate: r.endDate || '',
    endTime: r.endTime || '',
    personResponsible: r.personResponsible || '',
    personResponsibleRank: r.personResponsibleRank || '',
    leadPerson: r.leadPerson || '',
    leadPersonRank: r.leadPersonRank || '',
    officePermissionRequired: r.officePermissionRequired,
    officeApproved: r.officeApproved,
    officeRemarks: '',
    permitFormFiles: r.permitFormFiles.map((name) => ({name})),
    officePermitFiles: r.officePermitFiles.map((name) => ({name})),
    completedFormFiles: r.completedFormFiles.map((name) => ({name})),
    status: r.status || 'SAVE',
  })

  const handleSave = async (data: WorkPermitFormState, uploads: WorkPermitUploadFiles) => {
    try {
      if (editing) {
        const updated = await updateWorkPermit(editing.id, toPayload(data))
        await uploadAllAttachments(updated.id!, uploads)
      } else {
        const created = await createWorkPermit(toPayload(data))
        await uploadAllAttachments(created.id!, uploads)
      }
      toast.success('Work permit saved', {position: 'top-center'})
      setAddOpen(false)
      setEditOpen(false)
      setEditing(null)
      await loadWorkPermits()
    } catch (e: any) {
      console.error('Save work permit failed', e)
      toast.error('Save failed', {position: 'top-center'})
    }
  }

  const handleSubmit = async (data: WorkPermitFormState, uploads: WorkPermitUploadFiles) => {
    try {
      let id: number
      if (editing) {
        const updated = await updateWorkPermit(editing.id, toPayload(data))
        await uploadAllAttachments(updated.id!, uploads)
        const submitted = await submitWorkPermit(updated.id!)
        id = submitted.id!
      } else {
        const created = await createWorkPermit(toPayload(data))
        await uploadAllAttachments(created.id!, uploads)
        const submitted = await submitWorkPermit(created.id!)
        id = submitted.id!
      }
      toast.success('Work permit submitted', {position: 'top-center'})
      setAddOpen(false)
      setEditOpen(false)
      setEditing(null)
      await loadWorkPermits()
      return id
    } catch (e: any) {
      console.error('Submit work permit failed', e)
      toast.error('Submit failed', {position: 'top-center'})
    }
  }

  const uploadAllAttachments = async (id: number, uploads: WorkPermitUploadFiles) => {
    if (uploads.permitFormFiles?.length) {
      await uploadWorkPermitAttachments(id, 'FORM', uploads.permitFormFiles)
    }
    if (uploads.officePermitFiles?.length) {
      await uploadWorkPermitAttachments(id, 'OFFICE', uploads.officePermitFiles)
    }
    if (uploads.completedFormFiles?.length) {
      await uploadWorkPermitAttachments(id, 'COMPLETED', uploads.completedFormFiles)
    }
  }

  const openAllAttachmentsModal = async (record: WorkPermitRecord) => {
    setAttachmentsModal({
      visible: true,
      loading: true,
      title: 'Attachments',
      subtitle: record.permitName ? `Permit: ${record.permitName}` : null,
      items: [],
    })

    try {
      const types: {type: WorkPermitAttachmentType; label: string}[] = [
        {type: 'FORM', label: 'Form'},
        {type: 'OFFICE', label: 'Office'},
        {type: 'COMPLETED', label: 'Completed'},
      ]

      const lists = await Promise.all(
        types.map(async ({type}) => {
          const list = await listWorkPermitAttachments(record.id, type)
          return {type, list}
        })
      )

      const items = lists.flatMap(({type, list}) => {
        const typeLabel = types.find((t) => t.type === type)?.label ?? String(type)
        return list.map((it) => ({
          id: `${type}:${it.fileName}`,
          fileName: it.fileName,
          viewUrl: workPermitAttachmentViewUrl(record.id, type, it.fileName),
          downloadUrl: workPermitAttachmentDownloadUrl(record.id, type, it.fileName),
          type,
          typeLabel,
          sizeBytes: it.sizeBytes,
          mimeType: it.mimeType,
        }))
      })

      setAttachmentsModal({
        visible: true,
        loading: false,
        title: 'Attachments',
        subtitle: record.permitName ? `Permit: ${record.permitName}` : null,
        items,
      })
    } catch (e) {
      console.error('Failed to load attachments', e)
      toast.error('Failed to load attachments', {position: 'top-center'})
      setAttachmentsModal((prev) => ({...prev, loading: false}))
    }
  }

  const openFileViewer = (item: {fileName: string; viewUrl: string; downloadUrl: string}) => {
    setFileModal({
      visible: true,
      title: item.fileName,
      fileName: item.fileName,
      viewUrl: item.viewUrl,
      downloadUrl: item.downloadUrl,
    })
  }

  const handleApprove = async (record: WorkPermitRecord) => {
    try {
      await approveWorkPermit(record.id)
      toast.success('Permit approved', {position: 'top-center'})
      await loadWorkPermits()
    } catch (e: any) {
      console.error('Approve failed', e)
      toast.error('Approve failed', {position: 'top-center'})
    }
  }

  const handleClose = async (record: WorkPermitRecord) => {
    try {
      await closeWorkPermit(record.id)
      toast.success('Permit closed', {position: 'top-center'})
      await loadWorkPermits()
    } catch (e: any) {
      console.error('Close failed', e)
      toast.error('Close failed', {position: 'top-center'})
    }
  }

  const openView = async (record: WorkPermitRecord) => {
    try {
      const dto = await searchWorkPermits({vesselId: record.vesselId})
      const found = dto.find(d => d.id === record.id)
      if (found) {
        setViewing(found)
        setViewOpen(true)
      }
    } catch (e) {
      console.error('Failed to load permit details', e)
      toast.error('Failed to load permit details', {position: 'top-center'})
    }
  }

  const canClose = (r: WorkPermitRecord) => {
    if (r.status !== 'SUBMIT') return false
    if (r.officePermissionRequired && !r.officeApproved) return false
    if (!r.completedFormFiles?.length) return false
    if (!r.expectedEndDate || !r.expectedEndTime) return false
    const expectedEndAt = new Date(`${r.expectedEndDate}T${r.expectedEndTime}`)
    return new Date().getTime() >= expectedEndAt.getTime()
  }

  const renderStatusLabel = (r: WorkPermitRecord) => {
    if (!r.status) return '-'
    if (r.status === 'SAVE') return 'Saved'
    if (r.status === 'SUBMIT') {
      return r.officePermissionRequired && !r.officeApproved
        ? 'Pending Office Approval'
        : 'Submitted'
    }
    if (r.status === 'CLOSE') return 'Closed'
    return r.status
  }

  const isPermitExtended = (r: {startDate?: string; startTime?: string; expectedEndDate?: string; expectedEndTime?: string}) => {
    if (!r.startDate || !r.startTime || !r.expectedEndDate || !r.expectedEndTime) return false
    const s = new Date(`${r.startDate}T${r.startTime}`)
    const e = new Date(`${r.expectedEndDate}T${r.expectedEndTime}`)
    if (isNaN(s.getTime()) || isNaN(e.getTime())) return false
    const diffHours = (e.getTime() - s.getTime()) / (1000 * 60 * 60)
    return diffHours > 8.01
  }

  const handleSort = (
    column: keyof WorkPermitRecord | 'attachmentsCount' | 'officeStatus'
  ) => {
    setSortColumn((prev) => {
      if (prev === column) {
        setSortOrder((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'))
        return prev
      }
      setSortOrder('asc')
      return column
    })
  }

  const filteredRecords = useMemo(() => {
    if (!searchTerm.trim()) return records
    const term = searchTerm.toLowerCase()
    return records.filter((r) =>
      [
        r.permitName,
        r.formNumber,
        r.vesselName,
        r.personResponsible,
        r.leadPerson,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term))
    )
  }, [records, searchTerm])

  const sortedData = useMemo(() => {
    const data = [...filteredRecords]
    if (!sortColumn) return data

    const getSortValue = (row: WorkPermitRecord) => {
      if (sortColumn === 'attachmentsCount') {
        return (
          (row.permitFormFiles?.length || 0) +
          (row.officePermitFiles?.length || 0) +
          (row.completedFormFiles?.length || 0)
        )
      }
      if (sortColumn === 'officeStatus') {
        return row.officeApproved ? 1 : 0
      }
      return (row as any)[sortColumn]
    }

    data.sort((a, b) => {
      const aVal = getSortValue(a)
      const bVal = getSortValue(b)

      if (
        sortColumn === 'startDate' ||
        sortColumn === 'expectedEndDate' ||
        sortColumn === 'endDate'
      ) {
        const aTime = aVal ? new Date(String(aVal)).getTime() : 0
        const bTime = bVal ? new Date(String(bVal)).getTime() : 0
        const diff = aTime - bTime
        return sortOrder === 'asc' ? diff : -diff
      }

      if (typeof aVal === 'number' || typeof bVal === 'number') {
        const diff = Number(aVal || 0) - Number(bVal || 0)
        return sortOrder === 'asc' ? diff : -diff
      }

      const aStr = (aVal ?? '').toString().toLowerCase()
      const bStr = (bVal ?? '').toString().toLowerCase()

      if (aStr < bStr) return sortOrder === 'asc' ? -1 : 1
      if (aStr > bStr) return sortOrder === 'asc' ? 1 : -1
      return 0
    })

    return data
  }, [filteredRecords, sortColumn, sortOrder])

  const indexOfLastRecord = currentPage * rowsPerPage
  const indexOfFirstRecord = indexOfLastRecord - rowsPerPage
  const currentRecords = sortedData.slice(indexOfFirstRecord, indexOfLastRecord)
  const totalPages = Math.ceil(sortedData.length / rowsPerPage)

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page)
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10))
    setCurrentPage(1)
  }

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {name, value} = e.target
    setFilters((prev) => {
      const next = {...prev, [name]: value}
      if (name === 'companyId') {
        next.subcompanyId = ''
        next.vesselId = ''
      }
      if (name === 'subcompanyId') {
        next.vesselId = ''
      }
      return next
    })
    setCurrentPage(1)
  }

  const handleClearFilters = () => {
    setFilters({
      companyId: '',
      subcompanyId: '',
      vesselId: '',
      status: 'All',
      fromDate: '',
      toDate: '',
    })
    setSearchTerm('')
    setCurrentPage(1)
  }
  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
          <div className='card'>
            <div className='card-header border-0 pt-6 d-flex justify-content-between bg-white'>
              <div>
                <h3 className='card-label text-dark fw-bold'>Work Permits and Checklists</h3>
              </div>
              <div className='card-toolbar'>
                {canCreatePermit ? (
                  <button type='button' className='btn btn_primary' onClick={() => setAddOpen(true)}>
                    <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
                    Add Permit
                  </button>
                ) : null}
              </div>
            </div>

            <div className='card-body py-4 bg-white border-top'>
              <div className='row gx-3 gy-3 mb-4'>
              </div>

              <div className='row gx-3 gy-3'>
                <div className='col-md-3'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    Search
                  </label>
                  <input
                    type='text'
                    className='form-control'
                    placeholder='Permit, form no, vessel...'
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value)
                      setCurrentPage(1)
                    }}
                  />
                </div>

                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    Status
                  </label>
                  <SearchableSelect
                    className='form-select'
                    name='status'
                    value={filters.status}
                    onChange={handleFilterChange}
                  >
                    <option value='All'>All</option>
                    <option value='SAVE'>Saved</option>
                    <option value='SUBMIT'>Submitted</option>
                    <option value='CLOSE'>Closed</option>
                  </SearchableSelect>
                </div>

                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    From Date
                  </label>
                  <input
                    type='date'
                    className='form-control'
                    name='fromDate'
                    value={filters.fromDate}
                    onChange={handleFilterChange}
                  />
                </div>

                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    To Date
                  </label>
                  <input
                    type='date'
                    className='form-control'
                    name='toDate'
                    value={filters.toDate}
                    onChange={handleFilterChange}
                  />
                </div>

                <div className='col-md-1 d-flex align-items-end'>
                  <button type='button' className='btn btn-light btn-sm w-100' onClick={handleClearFilters}>
                    Clear
                  </button>
                </div>
              </div>
            </div>

            <div className='card-body py-4 bg-white border-top'>
              <div className='report-table table-responsive position-relative'>
                <div style={{overflowX: 'auto'}}>
                  <table className='table table-bordered align-middle'>
                    <thead className='table-header text-start'>
                      <tr>
                        <th className='text-center text-nowrap' style={{minWidth: '70px'}}>Sr/No</th>
                        <th style={{minWidth: '200px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Permit Name and Form Number</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('permitName')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'permitName'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{minWidth: '140px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Form No.</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('formNumber')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'formNumber'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{minWidth: '180px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Vessel</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('vesselName')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'vesselName'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{minWidth: '200px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Start</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('startDate')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'startDate'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{minWidth: '200px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Expected End</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('expectedEndDate')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'expectedEndDate'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{minWidth: '140px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Status</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('status')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'status'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{minWidth: '160px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Office</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('officeStatus')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'officeStatus'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{minWidth: '220px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Attachments</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('attachmentsCount')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'attachmentsCount'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{minWidth: '170px', textAlign: 'center'}}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className='table-body text-start'>
                      {isDataLoading ? (
                        <TableSkeleton rows={rowsPerPage || 8} cols={10} />
                      ) : currentRecords.length === 0 ? (
                        <tr>
                          <td colSpan={10} className='text-center text-muted py-5'>
                            No work permits found.
                          </td>
                        </tr>
                      ) : (
                        currentRecords.map((r, idx) => (
                          <tr key={r.id}>
                            <td className='text-center'>{indexOfFirstRecord + idx + 1}</td>
                            <td>{r.permitName}</td>
                            <td>{r.formNumber}</td>
                            <td>{r.vesselName}</td>
                            <td>
                              {r.startDate} {r.startTime}
                            </td>
                            <td>
                              {r.expectedEndDate || r.expectedEndTime ? (
                                <div className='d-flex align-items-center gap-2 flex-wrap'>
                                  <span>{`${r.expectedEndDate || ''} ${r.expectedEndTime || ''}`.trim()}</span>
                                  {isPermitExtended(r) && (
                                    <span className='badge badge-light-primary fs-8 px-2 py-1' title='Time slot extended (> 8 hours)'>
                                      + Extended
                                    </span>
                                  )}
                                </div>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td>
                              <span className={`badge ${statusBadgeClass(r.status)}`}>
                                {renderStatusLabel(r)}
                              </span>
                            </td>
                            <td>
                              {r.officePermissionRequired ? (
                                <span className={`badge ${r.officeApproved ? 'badge-light-success' : 'badge-light-warning'}`}>
                                  {r.officeApproved ? 'Approved' : 'Pending'}
                                </span>
                              ) : (
                                <span className='badge badge-light'>Not required</span>
                              )}
                            </td>
                            <td>
                              {(r.permitFormFiles.length || r.officePermitFiles.length || r.completedFormFiles.length) ? (
                                canOpenPermitFiles ? (
                                  <button
                                    type='button'
                                    className='btn btn-light-primary btn-sm d-inline-flex align-items-center text-nowrap'
                                    onClick={() => openAllAttachmentsModal(r)}
                                    title='View attachments'
                                  >
                                    <KTSVG
                                      path='/media/icons/duotune/files/fil003.svg'
                                      className='svg-icon-3 me-1'
                                    />
                                    View Files ({(r.permitFormFiles.length || 0) + (r.officePermitFiles.length || 0) + (r.completedFormFiles.length || 0)})
                                  </button>
                                ) : (
                                  <span className='text-muted'>-</span>
                                )
                              ) : (
                                <span className='text-muted'>-</span>
                              )}
                            </td>
                            <td className='text-nowrap'>
                              <div className='d-flex justify-content-left gap-1'>
                                {canReadPermit ? (
                                  <button
                                    className='btn btn-icon btn-sm'
                                    title='View'
                                    onClick={() => openView(r)}
                                  >
                                    <KTSVG path='/media/map/ph_eye.svg' className='svg-icon-3 text-primary' />
                                  </button>
                                ) : null}

                                {canUpdatePermit ? (
                                  <button
                                    className='btn btn-icon btn-sm'
                                    title='Edit'
                                    onClick={() => {
                                      setEditing(r)
                                      setEditOpen(true)
                                    }}
                                  >
                                    <KTSVG path='/media/map/edit-active.svg' className='svg-icon-3' />
                                  </button>
                                ) : null}

                                {r.status === 'SAVE' && canSubmitPermit && (
                                  <button
                                    className='btn btn-light-primary btn-sm'
                                    onClick={async () => {
                                      try {
                                        await submitWorkPermit(r.id)
                                        toast.success('Permit submitted', {position: 'top-center'})
                                        await loadWorkPermits()
                                      } catch (e: any) {
                                        toast.error('Submit failed', {position: 'top-center'})
                                      }
                                    }}
                                  >
                                    Submit
                                  </button>
                                )}

                                {r.status === 'SUBMIT' && r.officePermissionRequired && !r.officeApproved && canApprovePermit && (
                                  <button
                                    className='btn btn-light-warning btn-sm'
                                    onClick={() => handleApprove(r)}
                                  >
                                    Approve
                                  </button>
                                )}

                                {r.status === 'SUBMIT' && canClosePermit && (
                                  <button
                                    className='btn btn-light-success btn-sm'
                                    onClick={() => handleClose(r)}
                                    disabled={!canClose(r)}
                                  >
                                    Close
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div
                  className='pagination-wrapper d-flex justify-content-between align-items-center py-3 border-top'
                  style={{position: 'static', bottom: 0, backgroundColor: '#fff', zIndex: 10}}
                >
                  <div className='d-flex align-items-center'>
                    <span className='text-muted me-2'>Rows per page</span>
                    <SearchableSelect
                      className='form-select'
                      style={{
                        borderRadius: '20px',
                        width: '70px',
                        border: '1px solid #dee2e6',
                        fontSize: '14px',
                        padding: '4px 8px',
                      }}
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </SearchableSelect>
                  </div>

                  <div className='d-flex align-items-center'>
                    <span className='text-muted me-3' style={{fontSize: '14px'}}>
                      Showing{' '}
                      <strong>
                        {sortedData.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1}
                        -
                        {Math.min(currentPage * rowsPerPage, sortedData.length)}
                      </strong>{' '}
                      of <strong>{sortedData.length}</strong>
                    </span>

                    <nav>
                      <ul className='pagination pagination-sm mb-0' style={{gap: '2px'}}>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className='page-link text-muted'
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              padding: '8px 12px',
                              fontSize: '14px',
                              borderRadius: '6px',
                            }}
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            ‹
                          </button>
                        </li>

                        {(() => {
                          const pages = []
                          const showPages = 5
                          let startPage = Math.max(1, currentPage - 2)
                          let endPage = Math.min(totalPages, startPage + showPages - 1)

                          if (endPage - startPage < showPages - 1) {
                            startPage = Math.max(1, endPage - showPages + 1)
                          }

                          if (startPage > 1) {
                            pages.push(
                              <li key={1} className='page-item'>
                                <button
                                  className='page-link text-muted'
                                  style={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #dee2e6',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    minWidth: '40px',
                                    borderRadius: '6px',
                                  }}
                                  onClick={() => handlePageChange(1)}
                                >
                                  1
                                </button>
                              </li>
                            )

                            if (startPage > 2) {
                              pages.push(
                                <li key='ellipsis1' className='page-item disabled'>
                                  <span
                                    className='page-link border-0 text-muted'
                                    style={{backgroundColor: 'transparent', padding: '4px 8px'}}
                                  >
                                    ...
                                  </span>
                                </li>
                              )
                            }
                          }

                          for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                              <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                <button
                                  className='page-link text-muted'
                                  style={{
                                    backgroundColor: currentPage === i ? '#F4F9FF' : 'transparent',
                                    border: '1px solid #dee2e6',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    minWidth: '40px',
                                    borderRadius: '6px',
                                    outline: 'none',
                                    boxShadow: 'none',
                                  }}
                                  onClick={() => handlePageChange(i)}
                                >
                                  {i}
                                </button>
                              </li>
                            )
                          }

                          if (endPage < totalPages) {
                            if (endPage < totalPages - 1) {
                              pages.push(
                                <li key='ellipsis2' className='page-item disabled'>
                                  <span
                                    className='page-link border-0 text-muted'
                                    style={{backgroundColor: 'transparent', padding: '4px 8px'}}
                                  >
                                    ...
                                  </span>
                                </li>
                              )
                            }

                            pages.push(
                              <li key={totalPages} className='page-item'>
                                <button
                                  className='page-link text-muted'
                                  style={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #dee2e6',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    minWidth: '40px',
                                    borderRadius: '6px',
                                  }}
                                  onClick={() => handlePageChange(totalPages)}
                                >
                                  {totalPages}
                                </button>
                              </li>
                            )
                          }

                          return pages
                        })()}

                        <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                          <button
                            className='page-link text-muted'
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              padding: '8px 12px',
                              fontSize: '14px',
                              borderRadius: '6px',
                            }}
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || totalPages === 0}
                          >
                            ›
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <WorkPermitFormModal
        visible={addOpen}
        title='Add Work Permit'
        isCrew={shipboardUser}
        vessels={vesselsScoped}
        companies={companies}
        subcompanies={subcompanies}
        isTopLevel={isTopLevel}
        effectiveCompanyId={effectiveCompanyId}
        defaultVesselId={currentUser?.vessel?.id}
        permitNameOptions={permitNameOptions}
        onClose={() => setAddOpen(false)}
        onSave={handleSave}
        onSubmit={handleSubmit}
      />

      <WorkPermitFormModal
        visible={editOpen}
        title='Edit Work Permit'
        workPermitId={editing?.id}
        initial={editing ? mapRecordToFormState(editing) : undefined}
        isCrew={shipboardUser}
        vessels={vesselsScoped}
        companies={companies}
        subcompanies={subcompanies}
        isTopLevel={isTopLevel}
        effectiveCompanyId={effectiveCompanyId}
        defaultVesselId={currentUser?.vessel?.id}
        permitNameOptions={permitNameOptions}
        onClose={() => {
          setEditOpen(false)
          setEditing(null)
        }}
        onSave={handleSave}
        onSubmit={handleSubmit}
      />

      <WorkPermitAttachmentsModal
        visible={attachmentsModal.visible}
        loading={attachmentsModal.loading}
        title={attachmentsModal.title}
        subtitle={attachmentsModal.subtitle}
        items={attachmentsModal.items}
        onClose={() => setAttachmentsModal((prev) => ({...prev, visible: false}))}
        onViewFile={openFileViewer}
      />

      <FileViewerModal
        visible={fileModal.visible}
        title={fileModal.title}
        fileName={fileModal.fileName}
        viewUrl={fileModal.viewUrl}
        downloadUrl={fileModal.downloadUrl}
        onClose={() => setFileModal((prev) => ({...prev, visible: false}))}
      />

      <ViewWorkPermitModal
        visible={viewOpen}
        record={viewing}
        onCancel={() => {
          setViewOpen(false)
          setViewing(null)
        }}
      />

      <ToastContainer
        position='top-center'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{top: '5rem'}}
      />
    </div>
  )
}

export default WorkPermitsAndChecklistsPage

const mapDtoToRecord = (dto: QHSEWorkPermitDto): WorkPermitRecord => ({
  id: Number(dto.id),
  vesselId: Number(dto.vesselId),
  vesselName: dto.vesselName || '',
  permitName: dto.permitName || '',
  formNumber: dto.formNumber || '',
  startDate: dto.startDate || '',
  startTime: dto.startTime || '',
  expectedEndDate: dto.expectedEndDate || '',
  expectedEndTime: dto.expectedEndTime || '',
  endDate: dto.endDate || '',
  endTime: dto.endTime || '',
  personResponsible: dto.personResponsible || '',
  personResponsibleRank: dto.personResponsibleRank || '',
  leadPerson: dto.leadPerson || '',
  leadPersonRank: dto.leadPersonRank || '',
  officePermissionRequired: dto.officePermissionRequired ?? false,
  officeApproved: dto.officeApproved ?? false,
  permitFormFiles: dto.permitFormFiles || [],
  officePermitFiles: dto.officePermitFiles || [],
  completedFormFiles: dto.completedFormFiles || [],
  status: dto.status ?? 'SAVE',
  createdByRole: dto.createdByRole ?? 'OFFICE',
  submittedAt: dto.submittedAt ?? null,
  closedAt: dto.closedAt ?? null,
  companyGroupAdminId: null,
  companyAdminId: null,
})

const toPayload = (data: WorkPermitFormState): QHSEWorkPermitDto => ({
  vesselId: data.vesselId || 0,
  permitName: data.permitName,
  formNumber: data.formNumber,
  startDate: data.startDate,
  startTime: data.startTime,
  expectedEndDate: (data.expectedEndDate ? data.expectedEndDate : null) as any,
  expectedEndTime: (data.expectedEndTime ? data.expectedEndTime : null) as any,
  endDate: data.endDate || null,
  endTime: data.endTime || null,
  personResponsible: data.personResponsible,
  personResponsibleRank: data.personResponsibleRank,
  leadPerson: data.leadPerson,
  leadPersonRank: data.leadPersonRank,
  officePermissionRequired: data.officePermissionRequired,
  officeApproved: data.officeApproved,
  officeRemarks: data.officeRemarks,
})
